import { Anime } from '../types/anime';
import { getTodayKey } from './helpers';

/**
 * A show that just ended stays with the airing ones ("เพิ่งจบ") for three weeks, then moves to
 * "จบแล้ว". The day it ended is stamped on the show (finishedAt) the first time the app sees it end.
 */
export const RECENTLY_ENDED_DAYS = 21;

export type AiringState = 'airing' | 'recent' | 'ended';
export type AiringFilter = 'ALL' | 'AIRING' | 'AIRING_RECENT' | 'ENDED';

export const AIRING_FILTER_LABELS: Record<AiringFilter, string> = {
  AIRING_RECENT: 'กำลังฉาย + เพิ่งจบ',
  AIRING: 'กำลังฉาย',
  ENDED: 'จบแล้ว',
  ALL: 'ทุกสถานะ',
};

const DAY_MS = 24 * 60 * 60 * 1000;
const dayStart = (key: string) => new Date(`${key}T00:00:00`).getTime();
const daysBetween = (fromKey: string, toKey: string) =>
  Math.round((dayStart(toKey) - dayStart(fromKey)) / DAY_MS);
const toKey = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * Marked finished, or every episode of the season is out (12/12). More episodes than the saved
 * total (19/16) means the total was a guess, so that alone doesn't count as ended.
 */
export function hasEnded(anime: Pick<Anime, 'status' | 'latestEp' | 'totalEp'>): boolean {
  const aired = Number(anime.latestEp) || 0;
  const total = Number(anime.totalEp) || 0;
  return anime.status === 'จบแล้ว' || (aired > 0 && aired === total);
}

/** Days until a just-ended show moves to "จบแล้ว" (null = not in its "เพิ่งจบ" window) */
export function recentlyEndedDaysLeft(anime: Anime, today = getTodayKey()): number | null {
  if (!hasEnded(anime) || !anime.finishedAt) return null;
  const left = RECENTLY_ENDED_DAYS - daysBetween(anime.finishedAt, today);
  return left > 0 ? left : null;
}

export function airingState(anime: Anime, today = getTodayKey()): AiringState {
  if (!hasEnded(anime)) return 'airing';
  return recentlyEndedDaysLeft(anime, today) !== null ? 'recent' : 'ended';
}

export function matchesAiringFilter(anime: Anime, filter: AiringFilter, today = getTodayKey()) {
  if (filter === 'ALL') return true;
  const state = airingState(anime, today);
  if (filter === 'AIRING') return state === 'airing';
  if (filter === 'ENDED') return state === 'ended';
  return state !== 'ended';
}

const SEASON_END: Record<Anime['season'], [number, number]> = {
  WINTER: [2, 31],
  SPRING: [5, 30],
  SUMMER: [8, 30],
  FALL: [11, 31],
};

/** A finale this long after its season ended still counts as ending now (delays, e.g. Bleach Calamity) */
const DELAYED_FINALE_DAYS = 90;

/** Best guess of the day a show ended, for shows that ended before the app started tracking it */
function estimateFinishedAt(anime: Anime, today: string): string {
  const [month, day] = SEASON_END[anime.season] || SEASON_END.FALL;
  const seasonEnd = toKey(new Date(anime.year, month, day));
  const recent = (key?: string) => !!key && daysBetween(key, today) <= RECENTLY_ENDED_DAYS;
  // Last episode found just now for a season that only lately wrapped up: a delayed finale
  // (lastUpdatedAt is only set when a check finds a new episode)
  if (recent(anime.lastUpdatedAt) && daysBetween(seasonEnd, today) <= DELAYED_FINALE_DAYS) {
    return anime.lastUpdatedAt!;
  }
  // Its season wrapped up a while ago: it ended back then
  if (seasonEnd < today && !recent(seasonEnd)) {
    return anime.lastUpdatedAt && anime.lastUpdatedAt < today ? anime.lastUpdatedAt : seasonEnd;
  }
  // Ending now: the day its last episode was found, else today
  return recent(anime.lastUpdatedAt) ? anime.lastUpdatedAt! : today;
}

/** Stamp finishedAt on shows that just ended; clear it if a show turns out to continue */
export function stampFinishedDates(list: Anime[], today = getTodayKey()): { list: Anime[]; changed: boolean } {
  let changed = false;
  const next = list.map((anime) => {
    const ended = hasEnded(anime);
    if (ended && !anime.finishedAt) {
      changed = true;
      return { ...anime, finishedAt: estimateFinishedAt(anime, today) };
    }
    if (!ended && anime.finishedAt) {
      changed = true;
      const { finishedAt: _drop, ...rest } = anime;
      return rest as Anime;
    }
    return anime;
  });
  return { list: changed ? next : list, changed };
}
